import { FC, useMemo } from "react";
import type { PieLabelRenderProps } from "recharts";
import {
  Cell,
  Legend,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { Box, Center, Text } from "@/components/ui";
import { useLocalization, useThemeColors } from "@/hooks";

interface DataPoint {
  x: string;
  y: number;
}

interface PieChartProps {
  points: DataPoint[];
}

// Brand-token palette — vibrant purple shades first, dark navies late, one peach (no grey)
const PIE_PALETTE_OFFSETS = [
  "#a78bfa", // primary-400   (medium violet)
  "#7c3aed", // primary-600   (deep violet)
  "#ffa580", // secondary-300 (peach — sole warm accent)
  "#c4b5fd", // primary-300   (light lavender)
  "#6d28d9", // primary-700   (deepest violet)
  "#2d3558", // tertiary-500  (dark navy blue)
];

const RADIAN = Math.PI / 180;

function renderOuterLabel(props: PieLabelRenderProps) {
  const cx = Number(props.cx ?? 0);
  const cy = Number(props.cy ?? 0);
  const midAngle = Number(props.midAngle ?? 0);
  const outerRadius = Number(props.outerRadius ?? 0);
  const value = Number(props.value ?? 0);
  if (value < 3) return null;
  const radius = outerRadius + 20;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      dominantBaseline="central"
      fill="hsl(222,20%,16%)"
      fontSize={12}
      fontWeight={600}
      textAnchor={x > cx ? "start" : "end"}
      x={x}
      y={y}
    >
      {Math.round(value)}%
    </text>
  );
}

const PieChart: FC<PieChartProps> = ({ points }) => {
  const { i18n } = useLocalization();
  const themeColors = useThemeColors();

  const palette = useMemo(
    () => [themeColors.primary, ...PIE_PALETTE_OFFSETS],
    [themeColors.primary]
  );

  const pieData = useMemo(() => {
    if (points.length === 0) return [];

    const aggregated: Record<string, number> = {};
    points.forEach((point) => {
      aggregated[point.x] = (aggregated[point.x] ?? 0) + point.y;
    });

    const total = Object.values(aggregated).reduce((sum, v) => sum + v, 0);

    return Object.entries(aggregated).map(([label, value], idx) => ({
      color: palette[idx % palette.length],
      count: value,
      name: label,
      value: total > 0 ? Math.round((value / total) * 100) : 0,
    }));
  }, [points, palette]);

  if (pieData.length === 0) {
    return (
      <Center className="h-[450px] w-full">
        <Text className="text-typography-500">
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </Center>
    );
  }

  return (
    <Box className="h-[450px] w-full">
      {/* Explicit pixel height on the ResponsiveContainer avoids the
          recharts "width(-1) and height(-1)" warning on first paint —
          see the matching note in LineChart.web.tsx. */}
      <ResponsiveContainer height={450} minWidth={0} width="100%">
        <RechartsPieChart>
          <Pie
            cx="50%"
            cy="46%"
            data={pieData}
            dataKey="value"
            innerRadius="44%"
            label={renderOuterLabel}
            labelLine={false}
            nameKey="name"
            outerRadius="68%"
            paddingAngle={3}
            stroke="none"
          >
            {pieData.map((entry, idx) => (
              <Cell fill={entry.color} key={idx} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              border: "none",
              borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              fontFamily: "inherit",
              fontSize: 12,
            }}
            formatter={(value, _name, props) => [
              `${String(value)}%  ·  N=${props.payload.count}`,
              props.payload.name,
            ]}
          />
          <Legend
            formatter={(value: string) => (
              <span style={{ color: "hsl(222,20%,16%)", fontSize: 11 }}>
                {value}
              </span>
            )}
            iconSize={7}
            iconType="circle"
            itemSorter={null}
            verticalAlign="bottom"
          />
        </RechartsPieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default PieChart;
